import React, { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from '../../hooks/use-toast';
import api from '../../services/api';

const CUSTOM = '__custom';
const NONE = '__none';

export const VisaCostEditDialog = ({ row, meta, onClose, onSaved }) => {
  const [items, setItems] = useState([]);
  const [override, setOverride] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!row) return;
    setItems(row.items.map(({ legacy, ...i }) => ({ ...i, description_id: legacy ? '' : i.description_id })));
    setOverride(row.renewal_override || '');
    setNotes(row.notes || '');
  }, [row]);

  const upd = (idx, patch) => setItems(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const pickDesc = (idx, id) => {
    if (id === CUSTOM) return upd(idx, { description_id: '', description: '' });
    const d = meta.descriptions.find((x) => x.id === id);
    upd(idx, { description_id: id, description: d.name, amount: items[idx].amount || d.default_amount, cost_centre_id: items[idx].cost_centre_id || d.cost_centre_id || '' });
  };
  const total = items.reduce((s, i) => s + (Number(i.amount) || 0), 0);

  const save = async () => {
    if (items.some((i) => !String(i.description || '').trim())) {
      toast({ title: 'Missing description', description: 'Every line needs a description.', variant: 'destructive' });
      return;
    }
    if (!items.length && !window.confirm('No cost lines — this will set the visa cost (and Staff Cost) to AED 0. Continue?')) return;
    setSaving(true);
    try {
      await api.put(`/api/visa-costs/records/${row.employee_id}`, {
        items: items.map((i) => ({ description_id: i.description_id || '', description: i.description.trim(), amount: Number(i.amount) || 0, cost_centre_id: i.cost_centre_id || '' })),
        renewal_date_override: override || null, notes,
      });
      toast({ title: 'Saved', description: `Visa costs for ${row.name} updated (Staff Cost synced).` });
      onSaved();
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || 'Save failed', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="visa-cost-edit-dialog">
        <DialogHeader><DialogTitle>Visa costs — {row?.name} <span className="text-sm text-gray-500 font-normal">{row?.employee_code}</span></DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Visa expiry (from documents)</Label>
            <p className="text-sm font-medium mt-1">{row?.visa_expiry || '— not set —'}</p>
          </div>
          <div>
            <Label className="text-xs">Renewal date override (optional)</Label>
            <Input type="date" value={override} onChange={(e) => setOverride(e.target.value)} className="h-9" data-testid="vc-renewal-override" />
          </div>
        </div>
        <div className="space-y-2 mt-2">
          <div className="grid grid-cols-[1.4fr_1fr_110px_1fr_32px] gap-2 text-[11px] font-semibold uppercase text-gray-500">
            <span>Description</span><span>Custom text</span><span>Amount (AED)</span><span>Cost centre</span><span />
          </div>
          {items.map((it, idx) => (
            <div key={idx} className="grid grid-cols-[1.4fr_1fr_110px_1fr_32px] gap-2 items-center" data-testid={`vc-item-row-${idx}`}>
              <Select value={it.description_id || CUSTOM} onValueChange={(v) => pickDesc(idx, v)}>
                <SelectTrigger className="h-9" data-testid={`vc-item-desc-${idx}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={CUSTOM}>Custom description…</SelectItem>
                  {meta.descriptions.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input className="h-9" value={it.description} disabled={!!it.description_id} placeholder="Type description" onChange={(e) => upd(idx, { description: e.target.value })} data-testid={`vc-item-text-${idx}`} />
              <Input className="h-9" type="number" min={0} step="0.01" value={it.amount} onChange={(e) => upd(idx, { amount: e.target.value })} data-testid={`vc-item-amount-${idx}`} />
              <Select value={it.cost_centre_id || NONE} onValueChange={(v) => upd(idx, { cost_centre_id: v === NONE ? '' : v })}>
                <SelectTrigger className="h-9" data-testid={`vc-item-cc-${idx}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Unassigned</SelectItem>
                  {meta.cost_centres.map((c) => <SelectItem key={c.id} value={c.id}>{c.code} — {c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => setItems(items.filter((_, i) => i !== idx))} data-testid={`vc-item-remove-${idx}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={() => setItems([...items, { description_id: '', description: '', amount: 0, cost_centre_id: '' }])} data-testid="vc-add-item">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add cost line
          </Button>
        </div>
        <div>
          <Label className="text-xs">Notes</Label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="h-9" placeholder="Optional" data-testid="vc-notes" />
        </div>
        <DialogFooter className="items-center sm:justify-between">
          <p className="text-sm">Total visa cost: <b data-testid="vc-edit-total">AED {total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</b> <span className="text-xs text-gray-500">(syncs to Staff Cost)</span></p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="vc-save">{saving ? 'Saving…' : 'Save'}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
